import { useState } from "react";
import api from "@/lib/api";
import { X, Trash2, AlertTriangle } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const EMPTY = {
  name: "", category: "", unit: "", price: "", cogs: "", wholesale_cost: "", markup_pct: "",
  shipping_included: false, fulfillment_owner: "vendor", stock: 0,
  image_url: "", description: "", active: true,
  product_type: "cut", practices: [], members_only: false, limited_release: false,
};
const CATEGORIES = [["beef","Beef"],["chicken","Chicken"],["skincare","Skincare"],["wellness","Health & Wellness"]];
const FULFILLMENT_OWNERS = [["vendor","Vendor ships it"],["platform","We ship it"],["partner","Fulfillment partner ships it"]];
const PRODUCT_TYPES = [["cut","Individual cut"],["box","Curated box"],["bundle","Family bundle"],["bulk","Bulk order"],["subscription","Subscription product"],["retail","Retail product"]];
const PRACTICES = ["Grass-fed","Grass-finished","Pasture-raised","Grain-finished","No antibiotics","No added hormones","Regenerative","Dry-aged","Wild-caught","Heritage breed","Certified organic"];

export default function ProductEditDrawer({ product, onClose, onSaved, onDeleted }) {
  const isCreate = !product;
  const [form, setForm] = useState(isCreate ? EMPTY : {
    name: product.name || "",
    category: product.category || "",
    unit: product.unit || "",
    price: product.price ?? "",
    cogs: product.cogs ?? "",
    wholesale_cost: product.wholesale_cost ?? "",
    markup_pct: product.markup_pct ?? "",
    shipping_included: !!product.shipping_included,
    fulfillment_owner: product.fulfillment_owner || "vendor",
    stock: product.stock ?? 0,
    image_url: product.image_url || "",
    description: product.description || "",
    active: product.active !== false,
    product_type: product.product_type || "cut",
    practices: product.practices || [],
    members_only: !!product.members_only,
    limited_release: !!product.limited_release,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);

  const hasWholesale = form.wholesale_cost !== "" && form.wholesale_cost !== null && form.wholesale_cost !== undefined && !isNaN(parseFloat(form.wholesale_cost));
  const _mk = (form.markup_pct === "" || form.markup_pct === null || form.markup_pct === undefined || isNaN(parseFloat(form.markup_pct))) ? 25 : parseFloat(form.markup_pct);
  const retailPreview = hasWholesale ? (parseFloat(form.wholesale_cost) * (1 + _mk / 100)).toFixed(2) : "";

  const save = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        cogs: form.cogs === "" || form.cogs === null || form.cogs === undefined
          ? null : parseFloat(form.cogs),
        wholesale_cost: form.wholesale_cost === "" || form.wholesale_cost === null || form.wholesale_cost === undefined
          ? null : parseFloat(form.wholesale_cost),
        markup_pct: form.markup_pct === "" || form.markup_pct === null || form.markup_pct === undefined
          ? null : parseFloat(form.markup_pct),
        shipping_included: !!form.shipping_included,
        fulfillment_owner: form.fulfillment_owner || "vendor",
        stock: parseInt(form.stock, 10),
      };
      const { data } = isCreate
        ? await api.post(`/farm/inventory/products`, payload)
        : await api.put(`/farm/inventory/products/${product.id}`, payload);
      onSaved?.(data);
    } catch (err) {
      setError(err.response?.data?.detail || (isCreate ? "Create failed" : "Save failed"));
    } finally { setBusy(false); }
  };

  const del = async () => {
    setBusy(true); setError("");
    try {
      await api.delete(`/farm/inventory/products/${product.id}`);
      onDeleted?.(product.id);
    } catch (err) {
      setError(err.response?.data?.detail || "Delete failed");
      setConfirmDelete(false);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40" onClick={onClose} data-testid="product-edit-drawer">
      <div className="w-full max-w-2xl bg-white shadow-2xl overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex-1 min-w-0">
            <p className="overline">{isCreate ? "New product" : "Edit product"}</p>
            <h2 className="font-display text-2xl mt-1 truncate" data-testid="product-edit-title">
              {isCreate ? "Add a product to your farm" : product.name}
            </h2>
          </div>
          <button onClick={onClose} className="text-[var(--ff-text-muted)] hover:text-[var(--ff-text-primary)]" data-testid="product-edit-close" aria-label="Close">
            <X size={20}/>
          </button>
        </div>

        <form onSubmit={save} className="p-6 space-y-4">
          {error && <div className="bg-rose-50 border border-rose-200 p-3 rounded text-sm text-rose-800 flex gap-2" data-testid="product-edit-error"><AlertTriangle size={16}/> {error}</div>}

          {form.image_url && <img src={form.image_url} alt={form.name} className="w-full max-h-48 rounded object-cover" onError={e => { e.currentTarget.style.display = "none"; }}/>}

          <div>
            <span className="overline">Product photo</span>
            <div className="mt-2">
              <ImageUploader
                value={form.image_url}
                onChange={(url) => setForm({...form, image_url: url})}
                folder="products"
                testId="pe-image-upload"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <label className="block md:col-span-2">
              <span className="overline">Name</span>
              <input required className="input-base mt-2" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="pe-name"/>
            </label>
            <label className="block">
              <span className="overline">Category</span>
              <select required className="input-base mt-2" value={form.category} onChange={e => setForm({...form, category: e.target.value})} data-testid="pe-category">
                <option value="">Select…</option>
                {CATEGORIES.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="overline">Product type</span>
              <select className="input-base mt-2" value={form.product_type} onChange={e => setForm({...form, product_type: e.target.value})} data-testid="pe-product-type">
                {PRODUCT_TYPES.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="overline">Unit</span>
              <input required className="input-base mt-2" placeholder="lb, qt, jar, each…" value={form.unit} onChange={e => setForm({...form, unit: e.target.value})} data-testid="pe-unit"/>
            </label>
            <label className="block">
              <span className="overline">{hasWholesale ? "Member price (auto)" : "Price (USD)"}</span>
              <input required={!hasWholesale} readOnly={hasWholesale} type="number" step="0.01" min="0.01"
                className={`input-base mt-2 ${hasWholesale ? "bg-[var(--ff-muted)] cursor-not-allowed" : ""}`}
                value={hasWholesale ? retailPreview : form.price}
                onChange={e => setForm({...form, price: e.target.value})} data-testid="pe-price"/>
              {hasWholesale && <span className="block mt-1 text-[10px] text-[var(--ff-text-muted)]">Set automatically from wholesale + markup below.</span>}
            </label>
            <label className="block">
              <span className="overline">COGS / unit (USD)</span>
              <input type="number" step="0.01" min="0" className="input-base mt-2" placeholder="optional" value={form.cogs ?? ""} onChange={e => setForm({...form, cogs: e.target.value})} data-testid="pe-cogs"/>
              <span className="block mt-1 text-[10px] text-[var(--ff-text-muted)]">Used for ambassador net-profit. Leave blank to default to 55% of price.</span>
            </label>
            <label className="block">
              <span className="overline">Wholesale cost / unit (USD)</span>
              <input type="number" step="0.01" min="0" className="input-base mt-2" placeholder="what we pay you" value={form.wholesale_cost ?? ""} onChange={e => setForm({...form, wholesale_cost: e.target.value})} data-testid="pe-wholesale"/>
              <span className="block mt-1 text-[10px] text-[var(--ff-text-muted)]">Your invoiced cost per unit — this is your payout. Blank = legacy % commission.</span>
            </label>
            <label className="block">
              <span className="overline">Markup %</span>
              <input type="number" step="1" min="0" className="input-base mt-2" placeholder="25" value={form.markup_pct ?? ""} onChange={e => setForm({...form, markup_pct: e.target.value})} data-testid="pe-markup"/>
              <span className="block mt-1 text-[10px] text-[var(--ff-text-muted)]">Platform markup over wholesale. Default 25%. Set per product.</span>
            </label>
            {hasWholesale && (
              <div className="md:col-span-2 rounded-xl bg-[var(--ff-muted)] p-4 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm" data-testid="pe-price-preview">
                <span className="text-[var(--ff-text-muted)]">Your payout <strong className="text-[var(--ff-text-primary)]">${Number(form.wholesale_cost || 0).toFixed(2)}</strong></span>
                <span className="text-[var(--ff-text-muted)]">Markup <strong className="text-[var(--ff-text-primary)]">{_mk}%</strong></span>
                <span className="text-[var(--ff-text-muted)]">Member pays <strong className="text-[var(--ff-primary)] text-base" data-testid="pe-preview-retail">${retailPreview}</strong></span>
              </div>
            )}
            <label className="block">
              <span className="overline">Fulfillment</span>
              <select className="input-base mt-2" value={form.fulfillment_owner} onChange={e => setForm({...form, fulfillment_owner: e.target.value})} data-testid="pe-fulfillment-owner">
                {FULFILLMENT_OWNERS.map(([id, l]) => <option key={id} value={id}>{l}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-3 cursor-pointer md:self-end md:pb-3">
              <input type="checkbox" checked={form.shipping_included} onChange={e => setForm({...form, shipping_included: e.target.checked})} data-testid="pe-shipping-included"/>
              <span className="text-sm">Shipping included in wholesale cost</span>
            </label>
            <label className="block">
              <span className="overline">Stock on hand</span>
              <input required type="number" min="0" className="input-base mt-2" value={form.stock} onChange={e => setForm({...form, stock: e.target.value})} data-testid="pe-stock"/>
            </label>
            <label className="block md:col-span-2">
              <span className="overline">Image URL <span className="text-[10px] text-[var(--ff-text-muted)]">(auto-filled by upload above)</span></span>
              <input className="input-base mt-2 font-mono text-xs" placeholder="https://…" value={form.image_url} onChange={e => setForm({...form, image_url: e.target.value})} data-testid="pe-image"/>
            </label>
            <label className="block md:col-span-2">
              <span className="overline">Description</span>
              <textarea rows={4} className="input-base mt-2" value={form.description} onChange={e => setForm({...form, description: e.target.value})} data-testid="pe-description"/>
            </label>
            <div className="md:col-span-2">
              <span className="overline">Practices</span>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="pe-practices">
                {PRACTICES.map(pr => {
                  const on = form.practices.includes(pr);
                  return (
                    <button type="button" key={pr} onClick={() => setForm({...form, practices: on ? form.practices.filter(x => x !== pr) : [...form.practices, pr]})}
                      className={`px-3 py-1 rounded-full text-xs border transition-colors ${on ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)] hover:border-[var(--ff-primary)]"}`}>
                      {pr}
                    </button>
                  );
                })}
              </div>
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.members_only} onChange={e => setForm({...form, members_only: e.target.checked})} data-testid="pe-members-only"/>
              <span className="text-sm">Members-only product</span>
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={form.limited_release} onChange={e => setForm({...form, limited_release: e.target.checked})} data-testid="pe-limited"/>
              <span className="text-sm">Limited release</span>
            </label>
            <label className="flex items-center gap-3 md:col-span-2 cursor-pointer">
              <input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})} data-testid="pe-active"/>
              <span className="text-sm">Listed for sale (uncheck to hide without deleting)</span>
            </label>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-[var(--ff-border)]">
            <button type="submit" disabled={busy} className="btn-primary" data-testid="pe-save">
              {busy ? (isCreate ? "Creating…" : "Saving…") : (isCreate ? "Create product" : "Save changes")}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            {!isCreate && (
              <div className="ml-auto">
                {confirmDelete ? (
                  <div className="flex gap-2 items-center">
                    <span className="text-xs text-rose-700">Delete this product?</span>
                    <button type="button" onClick={del} disabled={busy} className="btn-primary text-sm bg-rose-700 hover:bg-rose-800" data-testid="pe-delete-confirm">Yes, delete</button>
                    <button type="button" onClick={() => setConfirmDelete(false)} className="btn-secondary text-sm">No</button>
                  </div>
                ) : (
                  <button type="button" onClick={() => setConfirmDelete(true)} className="text-rose-700 text-sm inline-flex items-center gap-1" data-testid="pe-delete-open">
                    <Trash2 size={14}/> Delete product
                  </button>
                )}
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}
